
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import {date, QBadge, QBtn, QSelect, QUploader} from "quasar"
import Axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from "axios"
import {isUrlHttp} from "@/utils/general"
import {Document, DocumentRequest, Mapping, MappingRequest} from "@/staff/Document"
import {Company, Plan, Platform} from "@/superannuation/PlanService"
import _ from "lodash"
import SelectDropDown from "@/components/SelectDropDown.vue"
import PopupDate from "@/components/PopupDate.vue"
import Message from "@/components/Message.vue"
import ExpandButton from "@/components/ExpandButton.vue"
import CloseButton from "@/components/CloseButton.vue"
import AddDeleteButton from "@/components/AddDeleteButton.vue"

@Component({
  components: {
    QBtn,
    CloseButton,
    AddDeleteButton,
    ExpandButton,
    PopupDate,
    Message,
    PageHeader,
    HelpIcon,
    QSelect,
    QUploader,
    QBadge,
    SelectDropDown,
  },
})
export default class DocumentView extends Vue {
  readonly metaTypes = ["Info", "Form", "Miscellaneous"]
  readonly allTypes = [...this.metaTypes, "InsuranceOptIn", "PlanSummary"]

  readonly mappingColumns = [
    {name: "id", label: "Mapping Id", field: "id"},
    {name: "company", label: "Employer", field: "company", sortable: true},
    {name: "document", label: "Document", field: "document", sortable: true},
    {name: "sequence", label: "Sequence", field: "sequence", sortable: true},
  ]

  readonly documentColumns = [
    {name: "platform", label: "Platform", field: "platform", sortable: true},
    {name: "plan", label: "Plan", field: "plan", sortable: true},
    {name: "company", label: "Employer", field: "company", sortable: true},
    {name: "filename", label: "Document", field: "title", sortable: true},
    {
      name: "modified",
      label: "Modified",
      field: "modified",
      sortable: true,
      format: (dateString: string) => date.formatDate(dateString, "Do MMM YY"),
    },
    {name: "pages", label: "Pages", field: "pages"},
    {
      name: "size",
      label: "Size",
      field: "size",
      sortable: true,
      format: (sizeString: string) =>
        `${Intl.NumberFormat().format(Math.round(Number(sizeString) / 1024))} KB`,
    },
  ]

  $refs: {
    companySelect: SelectDropDown
    uploader: QUploader
  }

  platformId: string | null = null
  planId: string | null = null
  companyId: string | null = null

  platforms: Array<Platform> | null = null
  plans: Array<Plan> | null = null
  companies: Array<Company> | null = null

  documents: Array<Document> | null = null
  mappings: Array<Mapping> | null = null
  maintainDocument: DocumentRequest | null = null
  maintainMapping: MappingRequest | null = null
  uploadFile: File | null = null

  readError = false
  writeError = false
  readMessage: string | null = null
  writeMessage: string | null = null
  rejection: string | null = null

  dataInProgress = false
  syncInProgress = false
  showDocument = false
  documentUpdate = false
  documentFile = false
  documentFilename: string | null = null
  showMapping = false
  mappingUpdate = false

  async created() {
    await this.load()
  }

  private async load() {
    await this.getPlatforms()
    await this.getPlans()
    await this.getCompanies()
    await this.getDocuments()
    await this.getMappings()
  }

  get error(): boolean {
    return this.readError || this.writeError
  }

  get message(): string | null {
    return this.writeMessage == null ? this.readMessage : this.writeMessage
  }

  private isMissingFileOrUrl(doc: DocumentRequest): boolean {
    const isDocument = _.isEmpty(doc!.url) || !isUrlHttp(doc!.url!)
    return (
      isDocument &&
      this.uploadFile === null &&
      (doc!.url !== this.documentFilename || !this.documentUpdate)
    )
  }

  get isDocumentDisabled(): boolean {
    const doc = this.maintainDocument
    const missingBasic =
      doc == null || doc.type === null || (this.isMetaDocument && _.isEmpty(doc.title))
    return missingBasic || this.isMissingFileOrUrl(doc)
  }

  get isMappingDisabled(): boolean {
    const map = this.maintainMapping
    return (
      _.some([map, map?.companyId, map?.documentId], _.isNull) ||
      map!.sequence <= 0 ||
      this.filteredMappings!.some(
        m =>
          m.id !== map!.mappingId &&
          ((m.sequence === map!.sequence && this.mappingUpdate) || m.documentId === map!.documentId)
      )
    )
  }

  private isSequenceValid(sequence: number): boolean | string {
    return (
      !this.filteredMappings!.some(
        m =>
          m.id !== this.maintainMapping!.mappingId && m.sequence === sequence && this.mappingUpdate
      ) || "Conflicts with another Sequence"
    )
  }

  private async getPlatforms() {
    await Axios.get(`/api/platforms?microsite=true&name=`).then((response: AxiosResponse) => {
      this.platforms = response.data
    })
  }

  private async getPlans() {
    await Axios.get(`/api/plan?memberless=true&microsite=true&name=`).then(
      (response: AxiosResponse) => {
        this.plans = response.data
      }
    )
  }

  private async getCompanies() {
    await Axios.get(`/api/companies?memberless=true&microsite=true&name=`).then(
      (response: AxiosResponse) => {
        this.companies = response.data
      }
    )
  }

  private async getDocuments() {
    if (this.unfiltered) {
      this.readMessage = null
      this.documents = null
    } else {
      this.dataInProgress = true
      await Axios.get(`/api/documents`, this.filters())
        .then((response: AxiosResponse) => {
          this.readMessage = null
          this.documents = response.data
        })
        .catch((reason: AxiosError<any>) => {
          this.readError = true
          this.readMessage = reason.response?.data.message
        })
        .finally(() => (this.dataInProgress = false))
    }
  }

  private async getMappings() {
    if (this.unfiltered) {
      this.readMessage = null
      this.mappings = null
    } else {
      this.dataInProgress = true
      await Axios.get(`/api/documents/mappings`, this.filters())
        .then((response: AxiosResponse) => {
          this.readMessage = null
          this.mappings = response.data
        })
        .catch((reason: AxiosError<any>) => {
          this.readError = true
          this.readMessage = reason.response?.data.message
        })
        .finally(() => (this.dataInProgress = false))
    }
  }

  private filters(): AxiosRequestConfig {
    return {
      params: {
        platformId: this.platformId,
        planId: this.planId,
        companyId: this.companyId,
      },
    }
  }

  get unfiltered(): boolean {
    return [this.platformId, this.planId, this.companyId].every(_.isNull)
  }

  private defaultMaintainDocument(document: Document) {
    this.maintainDocument = {
      documentId: document.documentId,
      platformId: document.platformId,
      planId: document.planId,
      companyId: document.companyId,
      type: document.type,
      title: this.metaTypes.includes(document.type) ? document.title : null,
      description: document.description,
      url: document.filename,
    }
    this.documentFile = document.file
    this.documentFilename = document.filename
  }

  private showUpdateDocument(document: Document) {
    this.defaultMaintainDocument(document)
    this.documentUpdate = true
    this.showDocument = true
    this.uploadFile = null
  }

  private showCreateDocument() {
    this.maintainDocument = this.newDocument()
    this.documentUpdate = false
    this.showDocument = true
    this.uploadFile = null
  }

  private newDocument(): DocumentRequest {
    return {
      documentId: null,
      platformId: this.platformId,
      planId: this.planId,
      companyId: this.companyId,
      type: this.metaTypes.find(Boolean)!,
      title: null,
      description: null,
      url: null,
    }
  }

  private defaultMaintainMapping(mapping: Mapping) {
    this.maintainMapping = {
      mappingId: mapping.id,
      companyId: mapping.companyId,
      documentId: mapping.documentId,
      sequence: mapping.sequence,
    }
  }

  private clearDocument() {
    this.maintainDocument!.url = null
    this.documentFile = false
  }

  get documentLabel(): string {
    return this.documentUpdate && this.documentFile ? "Document" : "External Url"
  }

  private showUpdateMapping(mapping: Mapping) {
    this.defaultMaintainMapping(mapping)
    this.mappingUpdate = true
    this.showMapping = true
  }

  private async showCreateMapping() {
    this.maintainMapping = this.newMapping()
    if (this.maintainMapping.companyId != null) {
      this.applyNewMappingSequence()
    }
    this.mappingUpdate = false
    this.showMapping = true
  }

  private newMapping(): MappingRequest {
    return {
      mappingId: null,
      companyId: this.companyId,
      documentId: null,
      sequence: 1,
    }
  }

  private documentThen = () => {
    this.writeError = false
    this.writeMessage = null
    void this.getDocuments()
    this.uploadFile = null
  }

  private mappingThen = () => {
    this.writeError = false
    this.writeMessage = null
    void this.getMappings()
  }

  get isDocumentUrl(): boolean {
    return !_.isEmpty(this.maintainDocument!.url) && isUrlHttp(this.maintainDocument!.url!)
  }

  private async persistDocument() {
    this.showDocument = false
    if (!this.isDocumentDisabled) {
      const form = new FormData()
      if (this.uploadFile !== null) {
        form.append("file", this.uploadFile)
      }

      if (this.documentUpdate && !this.isDocumentUrl) {
        this.maintainDocument!.url = null
      }
      const json = JSON.stringify(this.maintainDocument)
      const blob = new Blob([json], {type: "application/json"})
      form.append("document", blob)

      this.dataInProgress = true
      void (await Axios.post(`/api/documents`, form)
        .then(this.documentThen)
        .catch((reason: AxiosError<any>) => {
          this.writeError = true
          this.writeMessage = reason.response?.data.message
        })
        .finally(() => (this.dataInProgress = false)))
    }
  }

  private async persistMapping() {
    this.showMapping = false
    if (!this.isMappingDisabled) {
      this.dataInProgress = true
      void (await Axios.post(`/api/documents/mappings`, this.maintainMapping)
        .then(this.mappingThen)
        .catch((reason: AxiosError<any>) => {
          this.writeError = true
          this.writeMessage = reason.response?.data.message
        })
        .finally(() => (this.dataInProgress = false)))
    }
  }

  private platformSelected(platform: Platform | null) {
    this.platformId = platform == null ? null : platform.id
    if (platform != null) {
      this.planId = null
    }
    void this.getDocuments()
    void this.getMappings()
  }

  private planSelected(plan: Plan | null) {
    this.planId = plan == null ? null : plan.id
    if (plan != null) {
      this.companyId = null
    }
    void this.getDocuments()
    void this.getMappings()
  }

  private companySelected(company: Company | null) {
    this.companyId = company == null ? null : company.id
    void this.getDocuments()
    void this.getMappings()
  }

  get filteredPlans(): Array<Plan> {
    return this.plans!.filter(
      p =>
        this.maintainDocument!.platformId == null ||
        p.platform.id === this.maintainDocument!.platformId
    )
  }

  get filteredCompanies(): Array<Company> {
    const doc = this.maintainDocument
    return this.companies!.filter(
      c =>
        (doc!.platformId == null || c.plan.platform.id === doc!.platformId) &&
        (doc!.planId == null || c.plan.id === doc!.planId)
    )
  }

  get filteredMappingCompanies(): Array<Company> {
    return this.companies!.filter(
      c =>
        (this.companyId == null || c.id === this.companyId) &&
        (this.planId == null || c.plan.id === this.planId) &&
        (this.platformId == null || c.plan.platform.id === this.platformId)
    )
  }

  get filteredDocuments(): Array<Document> {
    if (this.maintainMapping!.companyId == null) return new Array<Document>()
    const company = this.companies!.find(c => c.id === this.maintainMapping!.companyId)!
    const mapped = new Set(
      this.filteredMappings
        .map(m => m.documentId)
        .filter(id => this.maintainMapping!.documentId !== id)
    )

    return this.documents!.filter(d => this.isMappable(company, d) && !mapped.has(d.documentId))
  }

  private isMappable(c: Company, d: Document) {
    return (
      d.companyId === c.id ||
      [d.platformId, d.planId, d.companyId].every(_.isNull) ||
      (d.planId === c.plan.id && d.companyId === null) ||
      (d.platformId === c.plan.platform.id && [d.planId, d.companyId].every(_.isNull))
    )
  }

  private document(mapping: Mapping): string | undefined {
    return this.documents?.find(d => d.documentId === mapping.documentId)?.filename
  }

  get filteredMappings(): Array<Mapping> {
    return this.mappings!.filter(m => m.companyId === this.maintainMapping!.companyId)
  }

  private applyNewMappingSequence() {
    const lastMapping = _.last(this.filteredMappings)
    this.maintainMapping!.sequence = (lastMapping ? lastMapping!.sequence : 0) + 1
  }

  private async setMappingCompany(option: Company | null) {
    if (option == null) {
      this.maintainMapping!.documentId = null
    }

    if (!this.mappingUpdate) {
      this.applyNewMappingSequence()
    }
  }

  private documentLink(pdf: Document) {
    return pdf.file ? `/api/documents/${pdf.documentId}` : pdf.filename
  }

  private isDocumentUnmapped(document: Document): boolean {
    return !this.mappings!.some(m => m.documentId === document.documentId)
  }

  get isMetaDocument(): boolean {
    return (
      this.maintainDocument!.type != null && this.metaTypes.includes(this.maintainDocument!.type)
    )
  }

  get isDocumentLevelReadOnly(): boolean {
    return this.documentUpdate && !this.isDocumentUrl && this.uploadFile === null
  }

  private rejected(rejectedEntries: Array<{failedPropValidation: string; file: File}>) {
    const entry = rejectedEntries.find(Boolean)
    this.writeError = true
    this.writeMessage = `File: '${entry!.file.name}' type: '${entry!.file.type}' did not pass: '${
      entry!.failedPropValidation
    }' validation constraint`
    this.uploadFile = null
  }

  private added(files: Array<File>) {
    this.writeError = false
    this.writeMessage = null
    this.uploadFile = files.find(Boolean)!
  }

  private removed() {
    this.uploadFile = null
  }

  private deleteThen = () => {
    void this.getDocuments()
    void this.getMappings()
  }

  private async deleteDocument(document: Document) {
    await Axios.delete(`/api/documents/${document.documentId}`)
      .then(this.deleteThen)
      .catch((reason: AxiosError<any>) => {
        this.writeError = true
        this.writeMessage = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  private async deleteMapping(mapping: Mapping) {
    await Axios.delete(`/api/documents/mappings/${mapping.id}`)
      .then(this.deleteThen)
      .catch((reason: AxiosError<any>) => {
        this.writeError = true
        this.writeMessage = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  get isCacheEvictDisabled(): boolean {
    return (
      this.dataInProgress ||
      this.syncInProgress ||
      (this.companyId == null && (this.platformId !== null || this.planId !== null))
    )
  }

  private async cacheEvict() {
    const config = {params: {companyId: this.companyId}}
    this.dataInProgress = true
    await Axios.delete("/api/companies/microsite/cacheEvict", config)
      .then(() => {
        this.writeError = false
        this.writeMessage = "Microsite Cache Refreshed"
      })
      .catch((reason: AxiosError<any>) => {
        this.writeError = true
        this.writeMessage = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  get isProduction(): boolean {
    return window.location.hostname === "employercentral.com.au"
  }

  get isSyncUATDisabled(): boolean {
    return this.dataInProgress || this.syncInProgress
  }

  private async syncUAT() {
    this.syncInProgress = true
    await Axios.post("/api/documents/syncUAT")
      .then(() => {
        this.writeError = false
        this.writeMessage = "Documents Synced with UAT"
      })
      .catch((reason: AxiosError<any>) => {
        this.writeError = true
        this.writeMessage = reason.response?.data.message
      })
      .finally(() => (this.syncInProgress = false))
  }
}
